import { Stack, useTheme } from "@mui/material";
import { useSetAtom } from "jotai";
import type { FC } from "react";
import { useFlag } from "../../../server/optimizely";
import { FixedFooter } from "../../generic/components/fixed-footer";
import { LeaveMomentPageContext } from "../../generic/components/leave-moment-page-context";
import type { Tags } from "../../merge-tag/types";
import { useFormSaveState } from "../hooks/use-form-save-state";

import { useNewsletterRedirect } from "../hooks/use-newsletter-redirect";
import type { ProgramsState } from "../hooks/use-programs";
import { useUnrecoverableErrorLogger } from "../hooks/use-unrecoverable-error-logger";
import { unsplashDownloadUrlAtom } from "../types/store";
import { ChannelAccordion } from "./accordion/channel-accordion";
import { MessageAccordion } from "./accordion/message-accordion";
import { ScheduleAccordion } from "./accordion/schedule-accordion";
import { SegmentAccordion } from "./accordion/segment-accordion";
import { BasicForm } from "./basic-form";
import { MomentFormButtons } from "./buttons/moment-form-buttons";
import { type OpenState, useAccordionState } from "./config";
import type { MomentFormArgs } from "./moment-form";
import { FooterBlobs } from "./roundabout/footer-blobs";
import { useRoundaboutContext } from "./roundabout/roundabout-context";

export const MomentFormInner: FC<
  MomentFormArgs & {
    mergeTags: Tags;
    reloadMergeTags: () => void;
    saveDataReady: boolean;
    programs: ProgramsState;
    onSaved: () => void;
  }
> = ({
  moment,
  isNewsletter,
  mergeTags,
  reloadMergeTags,
  connectionCategories,
  momentRestrictions,
  saveDataReady,
  programs,
  onSaved,
}) => {
  useNewsletterRedirect(moment, isNewsletter);
  useUnrecoverableErrorLogger();

  const setUnsplashDownloadUrl = useSetAtom(unsplashDownloadUrlAtom);
  const { userReadOnlyRole } = useRoundaboutContext();
  const formSaveState = useFormSaveState(userReadOnlyRole);
  const {
    state,
    setOpen: setChannelAccordionOpen,
    transition: transitionAccordion,
    AccordionToggle,
  } = useAccordionState();

  const theme = useTheme();
  const [whatsAppEnabled] = useFlag("whatsapp-communications");
  const channels = moment.composeMethods.filter(
    (channel) => channel !== "whatsapp" || whatsAppEnabled || moment.channel === "whatsapp",
  );

  return (
    <form>
      <LeaveMomentPageContext />

      <AccordionToggle />
      <BasicForm
        formDisabled={formSaveState.formDisabled["basic-form"]}
        setUnsplashDownloadUrl={setUnsplashDownloadUrl}
        programs={programs}
        sx={{ bgcolor: theme.palette.background.paper }}
      />
      <ChannelAccordion
        open={state.channel}
        setOpen={(open: OpenState) => setChannelAccordionOpen("channel", open)}
        formDisabled={formSaveState.formDisabled.channel}
        channels={channels}
        onConfirm={() => transitionAccordion("channel", "to")}
        lastFormAction={formSaveState.lastAction}
        newsletter={isNewsletter}
        sx={{ bgcolor: theme.palette.background.paper }}
        copyDisabled={formSaveState.formDisabled["draft-button"]}
        mergeTags={mergeTags}
      />
      <SegmentAccordion
        open={state.to}
        setOpen={(open: OpenState) => setChannelAccordionOpen("to", open)}
        formDisabled={formSaveState.formDisabled.segment}
        onConfirm={() => transitionAccordion("to", "when")}
        lastFormAction={formSaveState.lastAction}
        sx={{ bgcolor: theme.palette.background.paper }}
        connectionCategories={connectionCategories}
        restrictedSegments={momentRestrictions.segment}
        isNewsletter={isNewsletter}
      />
      <ScheduleAccordion
        open={state.when}
        setOpen={(open: OpenState) => setChannelAccordionOpen("when", open)}
        formDisabled={formSaveState.formDisabled.schedule}
        onConfirm={() => transitionAccordion("when", "message")}
        reloadMergeTags={reloadMergeTags}
        programs={programs}
        connectionCategories={connectionCategories}
        restrictedSegments={momentRestrictions.segment}
        lastFormAction={formSaveState.lastAction}
        sx={{ bgcolor: theme.palette.background.paper }}
      />
      <MessageAccordion
        open={state.message}
        setOpen={(open: OpenState) => setChannelAccordionOpen("message", open)}
        formDisabled={formSaveState.formDisabled.message}
        tags={mergeTags}
        lastFormAction={formSaveState.lastAction}
        sx={{ bgcolor: theme.palette.background.paper }}
        newsletter={isNewsletter}
        restrictedEmailFrom={momentRestrictions.email_from}
      />

      <FixedFooter noSidebar>
        <Stack direction="row-reverse" justifyContent="space-between" alignItems="center">
          <MomentFormButtons
            formSaveState={formSaveState}
            momentRestrictions={momentRestrictions}
            isNewsletter={isNewsletter}
            saveDataReady={saveDataReady}
            onSaved={onSaved}
          />
          <FooterBlobs />
        </Stack>
      </FixedFooter>
    </form>
  );
};
